import TextAreaInput from 'components/forms/inputs/rhf-inputs/TextAreaInput';
import { SectionCard } from 'components/GridItemList';
import { FormattedDate } from 'components/misc/FormattedDate';
import { SectionEdit, SectionEditForm } from 'components/section-edit/SectionEdit';
import { ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useUpdateInvoiceBillingNotesMutation } from 'store/api/invoices';
import { FeatureFlag } from 'types/features';
import { InvoiceData } from 'types/invoices';
import { cn } from 'utils/cn';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import {
    BILLING_INFO_TITLE_IDS,
    BillingInfoEntry,
    BillingInfoSource,
    BillingInfoType,
    billingInfoOwner,
    getBillingInfoEntries,
} from '../views/components/BillingInvoicingInfoAccordions';
import { BILLING_NOTES_SECTION_ID, useCanEditBillingNotes } from './invoiceSections';

// The payer's own note first, then the case client's, the case's and its service agreement's.
const SOURCE_ORDER: BillingInfoSource[] = ['payer', 'client', 'case', 'serviceAgreement'];

const entriesOf = (invoice: InvoiceData, type: BillingInfoType) =>
    getBillingInfoEntries(invoice, type).toSorted(
        (a, b) => SOURCE_ORDER.indexOf(a.source) - SOURCE_ORDER.indexOf(b.source)
    );

// The field keeps ten rows in the section, where a text area otherwise starts at one line.
// Ten rows; the card's one field reads as its own heading, its text area flush with it rather than hanging.
const TEN_ROWS_CLASS = cn(
    'col-span-full [--field-hang:0px] [&_textarea]:min-h-[15.525em]',
    '[&_label]:text-foreground [&_label]:text-sm [&_label]:leading-5 [&_label]:font-medium'
);

const CAPTION_CLASS = 'text-caption text-foreground-tertiary';

/** One note: where it is kept and on whom, an admin fee where one applies, the note, and when it last changed. */
function BillingInfoNote({
    invoice,
    entry,
    type,
}: {
    invoice: InvoiceData;
    entry: BillingInfoEntry;
    type: BillingInfoType;
}) {
    const owner = billingInfoOwner(invoice, entry);
    return (
        <div className="flex flex-col gap-1">
            <span className={cn(CAPTION_CLASS, 'flex flex-wrap items-center gap-x-1')}>
                <FormattedMessage id={BILLING_INFO_TITLE_IDS[entry.source][type]} />
                {owner ? (
                    <>
                        <span aria-hidden>·</span>
                        {owner}
                    </>
                ) : null}
            </span>
            {entry.hasAdminFeeAllowed ? (
                <p className="text-body1 m-0 font-medium">
                    <FormattedMessage id="invoices.billing-info.admin-fee-allowed" />
                </p>
            ) : null}
            {entry.adminFeePercentage != null ? (
                <p className="text-body1 m-0 font-medium">
                    <FormattedMessage
                        id="invoices.billing-info.admin-fee"
                        values={{ percentage: entry.adminFeePercentage }}
                    />
                </p>
            ) : null}
            {entry.content ? <p className="text-body1 m-0 wrap-anywhere whitespace-pre-line">{entry.content}</p> : null}
            <span className={cn(CAPTION_CLASS, 'tabular-nums')}>
                <FormattedDate value={entry.auditDates.lastModifiedAt || entry.auditDates.createdAt} fallback="" />
            </span>
        </div>
    );
}

/** The invoicing info (internal), read in the card and still read under the field while it edits. */
function InvoicingInfo({ invoice, entries }: { invoice: InvoiceData; entries: BillingInfoEntry[] }) {
    return (
        <>
            <span className={CAPTION_CLASS}>
                <FormattedMessage id="invoices.invoicing-info-internal.title" />
            </span>
            {entries.length ? (
                <div className="flex flex-col gap-3">
                    {entries.map((entry) => (
                        <BillingInfoNote key={entry.key} invoice={invoice} entry={entry} type="invoicing" />
                    ))}
                </div>
            ) : (
                <p className="text-meta text-muted-foreground m-0">
                    <FormattedMessage id="invoices.invoicing-info-internal.none" />
                </p>
            )}
        </>
    );
}

interface EditorProps {
    invoice: InvoiceData;
    onClose: () => void;
    /** What the card still reads under the field. */
    children: ReactNode;
}

/** The payer's billing notes, the one note the invoice edits: nothing is saved before Save, and Cancel drops it. */
function BillingNotesEditor({ invoice, onClose, children }: EditorProps) {
    const methods = useForm<{ billingNotes: string }>({
        defaultValues: { billingNotes: invoice.clientParty.billingNotes ?? '' },
    });
    const [updateInvoiceBillingNotes] = useUpdateInvoiceBillingNotesMutation();

    const onSave = async ({ billingNotes }: { billingNotes: string }) => {
        await updateInvoiceBillingNotes({
            invoiceId: invoice.id,
            partyId: invoice.clientParty.id,
            billingNotes,
        }).unwrap();
        onClose();
    };

    return (
        <SectionEditForm methods={methods} onSave={onSave} onCancel={onClose}>
            <TextAreaInput
                control={methods.control}
                name="billingNotes"
                label="invoices.billing-info.payer-billing-notes"
                minRows={10}
                enableAutofill
                wrapperClassName={TEN_ROWS_CLASS}
            />
            <p className={CAPTION_CLASS}>
                <FormattedMessage
                    id="invoices.side-column.payer-note-hint"
                    values={{ payer: invoice.clientParty.officialName ?? invoice.clientParty.name }}
                />
            </p>
            {children ? <div className="border-divider flex flex-col gap-1 border-t pt-3">{children}</div> : null}
        </SectionEditForm>
    );
}

/**
 * The side column's Billing notes: every note the invoice's payer, clients, cases and service agreements carry, at
 * full height, then the invoicing info (internal) under a rule. No card while there is nothing in either. Edit, or
 * the Payer card's "Edit payer billing notes", opens the payer's note in place, even while the card is not drawn.
 */
export function InvoiceBillingNotesCard({ invoice }: { invoice: InvoiceData }) {
    const isInvoicingInfoEnabled = useIsFeatureEnabled(FeatureFlag.INVOICING_INFO_ENABLED);
    const canEditBillingNotes = useCanEditBillingNotes(invoice);
    const notes = entriesOf(invoice, 'billing');
    const internal = isInvoicingInfoEnabled ? entriesOf(invoice, 'invoicing') : [];
    const invoicingInfo = isInvoicingInfoEnabled ? <InvoicingInfo invoice={invoice} entries={internal} /> : null;

    return (
        <SectionEdit
            id={BILLING_NOTES_SECTION_ID}
            titleId="matters.billing-notes"
            canEdit={canEditBillingNotes}
            editor={(close) => (
                <BillingNotesEditor invoice={invoice} onClose={close}>
                    {invoicingInfo}
                </BillingNotesEditor>
            )}
        >
            {(edit) =>
                notes.length || internal.length ? (
                    <SectionCard title={<FormattedMessage id="matters.billing-notes" />} action={edit}>
                        <ul className="m-0 flex list-none flex-col gap-3 p-0">
                            {notes.map((entry) => (
                                <li key={entry.key}>
                                    <BillingInfoNote invoice={invoice} entry={entry} type="billing" />
                                </li>
                            ))}
                            {invoicingInfo ? (
                                <li className="border-divider flex flex-col gap-1 not-first:border-t not-first:pt-3">
                                    {invoicingInfo}
                                </li>
                            ) : null}
                        </ul>
                    </SectionCard>
                ) : null
            }
        </SectionEdit>
    );
}
